<template>
  <transition
    name="ai-visibility-sidebar"
    appear
    @after-leave="$emit('after-close')"
  >
    <div
      v-if="open"
      class="ai-visibility-sidebar"
      role="presentation"
      @click="$emit('close')"
    >
      <aside
        class="ai-visibility-sidebar__panel"
        role="dialog"
        aria-modal="true"
        :aria-label="ariaLabel"
        :style="panelStyle"
        @click.stop
      >
        <header class="ai-visibility-sidebar__top">
          <button
            type="button"
            class="ai-visibility-sidebar__close"
            :aria-label="closeLabel"
            @click="$emit('close')"
          >
            <i class="material-icons">close</i>
          </button>
        </header>
        <slot />
      </aside>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'AiVisibilitySidebar',
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    ariaLabel: {
      type: String,
      required: true,
    },
    closeLabel: {
      type: String,
      default: 'Close sidebar',
    },
    width: {
      type: String,
      default: '720px',
    },
  },
  computed: {
    panelStyle() {
      return {
        width: `min(${this.width}, 100vw)`,
      }
    },
  },
  watch: {
    open: {
      immediate: true,
      handler(open) {
        if (open) {
          document.addEventListener('keyup', this.handleKeyup)
          return
        }

        document.removeEventListener('keyup', this.handleKeyup)
      },
    },
  },
  beforeDestroy() {
    document.removeEventListener('keyup', this.handleKeyup)
  },
  methods: {
    handleKeyup(event) {
      if (event.key === 'Escape') {
        this.$emit('close')
      }
    },
  },
}
</script>

<style lang="scss" scoped>

.ai-visibility-sidebar {
  position: fixed;
  z-index: 40;
  inset: 0;
  display: flex;
  justify-content: flex-end;
  background-color: rgba(0, 0, 0, 0.42);
}

.ai-visibility-sidebar__panel {
  display: flex;
  overflow-y: auto;
  flex-direction: column;
  gap: 28px;
  background: var(--v-cardBg-base);
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.2);
  color: var(--v-mainColor-base);
  padding: 20px;
}

.ai-visibility-sidebar__top {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
}

.ai-visibility-sidebar__close {
  display: inline-flex;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  color: var(--v-mainColor-base);
  cursor: pointer;
  padding: 0;
}

.ai-visibility-sidebar__close i {
  font-size: 14px;
  font-weight: 800;
}

.ai-visibility-sidebar__close:focus {
  outline: 2px solid var(--v-lightYellowColor-base);
  outline-offset: 2px;
}

.ai-visibility-sidebar-enter-active,
.ai-visibility-sidebar-leave-active {
  transition: background-color 220ms ease;
}

.ai-visibility-sidebar-enter-active .ai-visibility-sidebar__panel,
.ai-visibility-sidebar-leave-active .ai-visibility-sidebar__panel {
  transition: transform 260ms ease;
}

.ai-visibility-sidebar-enter,
.ai-visibility-sidebar-leave-to {
  background-color: rgba(0, 0, 0, 0);
}

.ai-visibility-sidebar-enter .ai-visibility-sidebar__panel,
.ai-visibility-sidebar-leave-to .ai-visibility-sidebar__panel {
  transform: translateX(100%);
}

@media (prefers-reduced-motion: reduce) {
  .ai-visibility-sidebar-enter-active,
  .ai-visibility-sidebar-leave-active,
  .ai-visibility-sidebar-enter-active .ai-visibility-sidebar__panel,
  .ai-visibility-sidebar-leave-active .ai-visibility-sidebar__panel {
    transition: none;
  }
}

@media (max-width: 680px) {
  .ai-visibility-sidebar__panel {
    gap: 22px;
  }
}
</style>
